import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";

import ListFilter from "components/ListFilter";

import { fetchClientsList } from "store/client/actions";
import { setClientFilterInitialized } from "store/helper/actions";

const ClientFilter = ({ onInitialized }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { list: clientsList } = useSelector(({ clientReducer }) => clientReducer);
  const { authObject } = useSelector(({ authReducer }) => authReducer);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    dispatch(fetchClientsList());
  }, [dispatch]);

  const allowedClients = useMemo(() => {
    if (!authObject) return [];
    
    if (authObject.isAdmin) {
      return clientsList;
    }
    
    if (authObject.clientRoles && authObject.clientRoles.length > 0) {
      const allowedClientIds = authObject.clientRoles.map(cr => 
        cr.client?._id || cr.client
      );
      
      return clientsList.filter(client => 
        allowedClientIds.some(id => {
          const clientId = typeof client._id === 'string' ? client._id : client._id?.toString();
          const allowedId = typeof id === 'string' ? id : id?.toString();
          return clientId === allowedId;
        })
      );
    }
    
    return [];
  }, [clientsList, authObject]);

  const clientsWithLabel = useMemo(() => {
    return allowedClients.map(client => ({
      ...client,
      displayLabel: `${client.code || 'N/A'} - ${client.name || 'No name'}`
    }));
  }, [allowedClients]);

  useEffect(() => {
    setIsInitialized(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!allowedClients.length && isInitialized) {
      setIsInitialized(false);
    }
  }, [allowedClients, isInitialized]);

  useEffect(() => {
    if (!authObject) return;
    if (isInitialized) return;
    
    if (!allowedClients.length) return;
    
    const searchParams = queryString.parse(location.search);
    const hasClientId = searchParams.clientId;
    const shouldAutoSelect = allowedClients.length === 1;
    if (shouldAutoSelect) {
      if (!hasClientId || hasClientId !== allowedClients[0]._id.toString()) {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('clientId', allowedClients[0]._id);
        history.replace({
          pathname: location.pathname,
          search: newSearchParams.toString()
        });
        return;
      }
    } else {
      if (hasClientId) {
        const isClientAllowed = allowedClients.some(client => 
          client._id.toString() === hasClientId
        );
        if (!isClientAllowed) {
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete('clientId');
          history.replace({
            pathname: location.pathname,
            search: newSearchParams.toString()
          });
          return;
        }
      }
    }
    
    if (!allowedClients.length) return;
    
    setIsInitialized(true);
    dispatch(setClientFilterInitialized());
    if (onInitialized) onInitialized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedClients, authObject, location.search, history, location.pathname, isInitialized]);

  return (
    <ListFilter
      options={clientsWithLabel}
      filterKey={"clientId"}
      selectedLabel={"All clients"}
      unselectedLabel={"All clients"}
      labelAccessor={"displayLabel"}
      valueAccessor={"_id"}
      showDefaultOption={allowedClients.length > 1}
    />
  );
};

export default ClientFilter;
